import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { OS_TOAST_UNDO_KEY, ToastService } from '@core/services/toast.service';
import { ToastMessageOptions } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { OsIconComponent } from '../os-icon/os-icon.component';
import { OsIconName } from '../os-icon/os-icon.paths';

const ICON: Record<string, OsIconName> = {
	success: 'check',
	info: 'info',
	warn: 'alerta',
	error: 'alerta',
};

/**
 * Onde os avisos aparecem. Fica uma vez só, na raiz do app.
 *
 * Desenha o aviso com o ícone da situação e, quando o aviso traz uma ação de
 * desfazer, o botão "Desfazer". A tela nunca usa `p-toast`: fala com o
 * `ToastService`.
 */
@Component({
	selector: 'os-toast-host',
	imports: [Toast, OsIconComponent],
	template: `
		<p-toast position="top-right">
			<ng-template #message let-message>
				<span class="os-toast__icon" [class]="'os-toast__icon--' + message.severity">
					<os-icon [name]="icon(message)" [size]="20" />
				</span>
				<span class="os-toast__text">
					@if (message.summary) {
						<span class="os-toast__summary">{{ message.summary }}</span>
						@if (message.detail) {
							<span class="os-toast__detail">{{ message.detail }}</span>
						}
					} @else {
						<span class="os-toast__summary">{{ message.detail }}</span>
					}
				</span>
			</ng-template>
		</p-toast>

		<p-toast [key]="undoKey" position="bottom-center">
			<ng-template #message let-message>
				<span class="os-toast__text">
					@if (message.summary) {
						<span class="os-toast__summary">{{ message.summary }}</span>
						@if (message.detail) {
							<span class="os-toast__detail">{{ message.detail }}</span>
						}
					} @else {
						<span class="os-toast__summary">{{ message.detail }}</span>
					}
				</span>
				<button class="os-toast__undo" (click)="undo(message)" type="button">
					<os-icon [size]="16" name="desfazer" />
					Desfazer
				</button>
			</ng-template>
		</p-toast>
	`,
	styles: `
		:host {
			display: contents;
		}

		.os-toast__icon {
			display: inline-flex;
			flex-shrink: 0;
			color: var(--os-ink-soft);

			&--success {
				color: var(--os-ok);
			}

			&--warn {
				color: var(--os-warn);
			}

			&--error {
				color: var(--os-risk);
			}
		}

		.os-toast__text {
			display: flex;
			flex: 1;
			flex-direction: column;
			gap: 2px;
			min-width: 0;
		}

		.os-toast__summary {
			color: var(--os-ink);
			font-size: var(--os-fs-14);
			font-weight: 700;
		}

		.os-toast__detail {
			color: var(--os-ink-soft);
			font-size: var(--os-fs-13);
		}

		.os-toast__undo {
			display: inline-flex;
			flex-shrink: 0;
			align-items: center;
			gap: 6px;
			align-self: center;
			height: 32px;
			padding: 0 12px;
			border: 0;
			border-radius: var(--os-radius-sm);
			background: var(--os-surface-sunken);
			color: var(--os-ink);
			font: inherit;
			font-size: var(--os-fs-13);
			font-weight: 700;
			cursor: pointer;

			&:hover {
				background: var(--os-border);
			}
		}
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OsToastHostComponent {
	private readonly toast = inject(ToastService);
	readonly undoKey = OS_TOAST_UNDO_KEY;

	icon(message: ToastMessageOptions): OsIconName {
		return ICON[message.severity ?? 'info'] ?? 'info';
	}

	undo(message: ToastMessageOptions): void {
		const action = (message.data as { undo?: () => void } | undefined)?.undo;
		this.toast.dismissUndo();
		action?.();
	}
}
